import React, { useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { HeaderNavGroup, NavItem } from '../../utils/roles';
import { Role } from '../../types';
import { usePermissions } from '../../hooks/usePermissions';

interface NavDropdownProps {
  group: HeaderNavGroup;
  role: Role;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  icons: Record<string, React.ReactNode>;
}

export const NavDropdown: React.FC<NavDropdownProps> = ({ group, role, isOpen, onToggle, onClose, icons }) => {
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const { canView: rbacCanView } = usePermissions();

  const filterItem = (item: NavItem) => {
    const slug = item.path.replace('/', '');
    const rbacView = rbacCanView(slug);
    if (rbacView !== undefined) return rbacView;
    return item.check(role);
  };

  // Direct link (no dropdown)
  if (group.path) {
    const isActive = location.pathname === group.path || location.pathname.startsWith(group.path + '/');
    return (
      <NavLink
        to={group.path}
        className={`px-3 py-2 text-sm font-medium transition-colors rounded-md ${
          isActive
            ? 'text-white border-b-2 border-brand-500'
            : 'text-gray-300 hover:text-white hover:bg-white/5'
        }`}
      >
        {group.label}
      </NavLink>
    );
  }

  // Collect all visible items to check if dropdown should render
  const flatItems = [
    ...(group.items || []),
    ...(group.columns || []).flatMap(c => c.items),
  ].filter(filterItem);

  if (flatItems.length === 0) return null;

  const hasActiveChild = flatItems.some(item =>
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  // Two-column layout if group has columns
  const hasColumns = group.columns && group.columns.length > 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-md ${
          hasActiveChild || isOpen
            ? 'text-white border-b-2 border-brand-500'
            : 'text-gray-300 hover:text-white hover:bg-white/5'
        }`}
      >
        {group.label}
        <svg
          className={`w-3.5 h-3.5 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute top-full left-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 animate-dropdown ${
            hasColumns && group.columns!.length >= 3 ? 'min-w-[560px]' : hasColumns ? 'min-w-[320px]' : 'min-w-[200px]'
          }`}
        >
          {hasColumns && (
            <div className={`grid gap-x-6 p-4 ${group.columns!.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {group.columns!.map(col => {
                const visibleItems = col.items.filter(filterItem);
                if (visibleItems.length === 0) return null;
                return (
                  <div key={col.label}>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
                      {col.label}
                    </p>
                    {visibleItems.map(item => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${
                            isActive
                              ? 'text-brand-400 bg-brand-500/10'
                              : 'text-gray-200 hover:text-white hover:bg-white/5'
                          }`
                        }
                      >
                        {icons[item.icon]}
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          {/* Flat items (below columns if both exist, or standalone) */}
          {group.items && group.items.filter(filterItem).length > 0 && (
            <div className={`${hasColumns ? 'border-t border-gray-700' : ''} p-2`}>
              {group.items.filter(filterItem).map(item => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                      isActive
                        ? 'text-brand-400 bg-brand-500/10'
                        : 'text-gray-200 hover:text-white hover:bg-white/5'
                    }`
                  }
                >
                  {icons[item.icon]}
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
